import { z } from 'zod';

import { replacementReasonSchema } from '@/features/requests';

/** One row of the per-order Replacement Requests listing
 *  (`GET /orders/admin/:orderId/replacement-requests`).
 *
 *  **No `withInstallation`**: it appeared in the Sprint-4 stub and in no AC, and the deployed
 *  `ReplacementRequestListItem` has no such member — it exists only on the detail response. A
 *  `.nullish()` placeholder would put a permanently-undefined field on the parsed type and
 *  document a stub that no longer exists, so it is dropped instead; zod strips the key if a stale
 *  deployment still sends it (ADO 119316, review of PR #359).
 *
 *  `unitIndex` is parsed-and-unused here: it identifies which unit of a multi-quantity line item
 *  the request is against, rendered by ticket 06's Ticket Preview. */
export const orderReplacementRowSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  /** `order_items.productCategory` is `varchar null` and the admin service returns it verbatim, so
   *  a **bundle** line item legitimately has none. Non-nullable here rejected the whole 200 and the
   *  drawer showed its load-error copy over a populated payload (ADO 119316); the same undefined
   *  query then hid Claim Warranty (ADO 119354). `?? '—'` at every render site, exactly as the
   *  sibling warranty listing already does. */
  category: z.string().nullable(),
  unitIndex: z.number(),
  /** The line item's total unit count, so the row can suffix the unit number exactly as the intake
   *  dropdown and the Ticket Preview do (AC 112366). Added by BE on FLAG-3 (112365, 2026-07-29);
   *  `nullish` so a deployment that predates it degrades to the bare item name rather than failing
   *  the parse. */
  quantity: z.number().nullish(),
  status: z.enum(['pending', 'approved', 'rejected']),
  requestedAt: z.string(),
});

/** The whole listing response. `isWithinWindow` is the backend's replacement-window verdict — the
 *  frontend holds no opinion about the day count (ADR-0022; BE flag FLAG-1). */
export const orderReplacementListSchema = z.object({
  isWithinWindow: z.boolean(),
  requests: z.array(orderReplacementRowSchema),
});

/** One evidence image uploaded with the request. `sortOrder` is parsed but unused — the array
 *  already arrives ordered; re-sorting client-side would be a second source of truth. */
export const orderReplacementImageSchema = z.object({
  id: z.string(),
  /** Nullable because the BE presigns at read time and answers `null` when that throws
   *  (`replacement-shared.service.resolveImageUrl`). An image with no url cannot be shown or
   *  previewed, so the drawer drops it rather than rendering a broken thumbnail — one unshowable
   *  evidence photo must not take the whole detail down with it (ADO 119316, same defect class). */
  url: z.string().nullable(),
  originalFileName: z.string().nullable(),
  sortOrder: z.number(),
});

/** Read-only detail behind the Ticket Preview drawer (stub
 *  `GET /orders/admin/:orderId/replacement-requests/:requestId`).
 *
 *  Not parsed, deliberately: `reasonLabel` (we render `REPLACEMENT_REASONS[reason]` so one casing
 *  wins — the stub's "Item Arrived Damaged" disagrees with our "Item arrived damaged"); `imageUrl`
 *  (the product photo — the AC asks for evidence images, i.e. `images[]`); `requestedBy`/
 *  `resolvedBy` (no actor in any Figma frame or AC); `orderId`, `withInstallation`,
 *  `hasApprovedInstallation` (no AC). zod strips unknown keys, so they cost nothing.
 *
 *  `resolvedAt` stays nullable even though BE confirmed it is always set once resolved
 *  (ADO 112368, 2026-07-29) — the timeline derivation guards on it so a bad payload cannot render
 *  a terminal entry stamped `Invalid Date`. */
export const orderReplacementDetailSchema = z.object({
  id: z.string(),
  itemName: z.string(),
  /** Nullable for the same reason as the listing row above — a bundle line item carries no
   *  `productCategory` (ADO 119316). */
  category: z.string().nullable(),
  unitIndex: z.number(),
  quantity: z.number(),
  reason: z.enum([
    'item_arrived_damaged',
    'missing_parts_or_accessories',
    'incorrect_item_delivered',
    'defective_or_malfunctioning',
    'poor_quality_not_as_pictured',
    'others',
  ]),
  description: z.string().nullable(),
  status: z.enum(['pending', 'approved', 'rejected']),
  requestedAt: z.string(),
  resolvedAt: z.string().nullable(),
  images: z.array(orderReplacementImageSchema),
  rejectionReason: z.string().nullable(),
  replacementOrderId: z.string().nullable(),
  replacementOrderNumber: z.string().nullable(),
});

/** One eligible line item (Sprint-4 stub `GET …/replacement-eligible-items`). `eligibleUnits` is
 *  gap-preserving — units with a Pending or Approved request are simply absent and the survivors
 *  keep their original numbers (ADR-0022). `kind` is carried so the wire shape is documented; a
 *  bundle needs no special casing because the endpoint returns it as one entry. */
export const replacementEligibleItemSchema = z.object({
  orderItemId: z.string(),
  kind: z.string(),
  productName: z.string(),
  imageUrl: z.string().nullable(),
  quantity: z.number(),
  eligibleUnits: z.array(z.number()),
});

export const replacementEligibleItemsSchema = z.array(replacementEligibleItemSchema);

/** A file that finished uploading (the `<UploadField>` committed value shape). Declared locally, as
 *  `features/{orders,devices,bundles,components}/schemas.ts` each do. */
const uploadedFileSchema = z.object({
  id: z.string(),
  url: z.string(),
  name: z.string(),
  size: z.number(),
  contentType: z.string(),
});

/**
 * Intake form values. `unit` is the composite `${orderItemId}#${unitIndex}` option value, split at
 * submit (ADR-0022) — one field keeps `<SelectField>` untouched and the encoding pure.
 *
 * Description is optional for five reasons and mandatory for **Others** (AC 112366). That is a
 * single object-level `superRefine` raising its issue on the `description` path, not a six-branch
 * discriminated union: the field is always mounted, so this also sidesteps the freshly-mounted-field
 * `trigger` race hit on the discount tier form (grilling decision 8).
 */
export const requestReplacementSchema = z
  .object({
    unit: z.string().min(1, 'Select an item'),
    reason: replacementReasonSchema,
    description: z.string().max(2000),
    images: z
      .array(uploadedFileSchema)
      .min(1, 'Add at least one image')
      .max(5, 'You can upload a maximum of 5 images'),
  })
  .superRefine((values, ctx) => {
    if (values.reason === 'others' && values.description.trim().length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['description'],
        message: 'Description is required',
      });
    }
  });
